/**
 * EMAIL 템플릿 미리보기 패널 (ID-226).
 *
 * - is_html=true: 변수 치환 후 DOMPurify sanitize 한 HTML 을 렌더(운영자 화면 XSS 차단).
 * - is_html=false: 변수 치환된 plain-text 를 `white-space: pre-wrap` 로 그대로 노출(기존 동작).
 * 제목(subject)도 변수 치환해 함께 보여준다(EMAIL 은 subject 가 본문과 함께 발송됨).
 */
import { useMemo } from 'react';
import type { TemplateVariable } from '../lib/kakaoTemplate';
import {
  renderEmailHtmlPreview,
  renderPlainPreview,
} from '../lib/emailHtmlPreview';

export interface EmailPreviewProps {
  subject: string;
  body: string;
  variables: ReadonlyArray<TemplateVariable>;
  /** true 면 본문을 HTML 로 렌더(sanitize 후), false 면 plain-text. */
  isHtml: boolean;
  /** 제목/본문 라벨(i18n). */
  subjectLabel: string;
  bodyLabel: string;
  emptyLabel: string;
}

export const EmailPreview = ({
  subject,
  body,
  variables,
  isHtml,
  subjectLabel,
  bodyLabel,
  emptyLabel,
}: EmailPreviewProps) => {
  const subjectPreview = useMemo(
    () => renderPlainPreview(subject, variables),
    [subject, variables],
  );

  // is_html 일 때만 sanitize 비용 발생. plain 은 텍스트 치환만.
  const htmlPreview = useMemo(
    () => (isHtml ? renderEmailHtmlPreview(body, variables) : ''),
    [isHtml, body, variables],
  );
  const plainPreview = useMemo(
    () => (isHtml ? '' : renderPlainPreview(body, variables)),
    [isHtml, body, variables],
  );

  const hasBody = body.trim().length > 0;

  return (
    <div className="flex flex-col gap-3" data-testid="email-preview">
      {/* 제목 미리보기 */}
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-medium" style={{ color: 'var(--content-muted)' }}>
          {subjectLabel}
        </span>
        <div
          className="rounded-md px-2.5 py-1.5 text-[13px] font-semibold"
          style={{ background: 'var(--surface-1)', color: 'var(--content-strong)' }}
        >
          {subjectPreview.trim() ? subjectPreview : <span style={{ color: 'var(--content-muted)' }}>—</span>}
        </div>
      </div>

      {/* 본문 미리보기 */}
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-medium" style={{ color: 'var(--content-muted)' }}>
          {bodyLabel}
        </span>
        {!hasBody ? (
          <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
            {emptyLabel}
          </p>
        ) : isHtml ? (
          // sanitize 된 HTML — 활성 콘텐츠(script/onerror 등) 제거됨. 미리보기 격리 박스.
          <div
            className="overflow-auto rounded-md p-3"
            style={{ background: '#ffffff', color: '#1a1a1a', maxHeight: 520, border: '1px solid var(--divider-strong)' }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: htmlPreview }}
          />
        ) : (
          <pre
            className="overflow-auto rounded-md p-3 text-[13px]"
            style={{
              background: 'var(--surface-0)',
              color: 'var(--content-default)',
              maxHeight: 520,
              border: '1px solid var(--divider-strong)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
            }}
          >
            {plainPreview}
          </pre>
        )}
      </div>
    </div>
  );
};
